import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import { getSalePackageOptions } from "./salePackageUtils";

const validatePackageId = (packageId) => {
  if (typeof packageId !== "string" || !packageId.trim()) {
    throw new Error("Enter a package ID.");
  }
  return packageId.trim();
};

export const createSalePackage = async (name, IsActive = "Active", packageId) => {
  const enteredName = typeof name === "string" ? name.trim() : "";
  if (!enteredName) throw new Error("Enter a package name.");
  const enteredPackageId = validatePackageId(packageId);
  if (!["Active", "Inactive"].includes(IsActive)) throw new Error("Invalid package status.");
  const packagesRef = collection(db, "salePackages");
  const existing = await getDocs(packagesRef);
  if (existing.docs.some((item) =>
    String(item.data().name || "").trim().toLowerCase() === enteredName.toLowerCase())) {
    throw new Error("This package already exists. Update its status in the list.");
  }
  const ref = doc(packagesRef);
  await setDoc(ref, { id: ref.id, name: enteredName, packageId: enteredPackageId, IsActive });
};

export const updateSalePackage = async (id, IsActive, packageId) => {
  const enteredPackageId = validatePackageId(packageId);
  if (!["Active", "Inactive"].includes(IsActive)) throw new Error("Invalid package status.");
  await updateDoc(doc(db, "salePackages", id), { id, IsActive, packageId: enteredPackageId });
};

export const useSalePackages = () => {
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    return onSnapshot(collection(db, "salePackages"), (snapshot) => {
      setPackages(snapshot.docs.map((item) => ({ ...item.data(), id: item.id, packageId: item.data().packageId || item.id }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
      setIsLoading(false);
      setError(null);
    }, () => {
      setError("Unable to load sale packages. Please try again.");
      setIsLoading(false);
    });
  }, [revision]);

  return { packages, isLoading, error, retry: () => setRevision((value) => value + 1) };
};

export const useSalePackageOptions = (currentValue) => {
  const catalog = useSalePackages();
  return {
    ...catalog,
    options: getSalePackageOptions(catalog.packages, currentValue),
    disabled: catalog.isLoading || Boolean(catalog.error),
    message: catalog.isLoading ? "Loading sale packages..." : catalog.error ||
      (catalog.packages.some((item) => item.IsActive === "Active") ? "" : "No active sale packages."),
  };
};
